import {useEffect, useState} from "react";
import SidebarHeader from "../components/learn/SidebarHeader";
import NewTreeButton from "../components/learn/NewTreeButton";
import TreeList from "../components/learn/TreeList";
import MobileSidebarMenu from "../components/learn/MobileSidebarMenu";
import ProfileButton from "../components/learn/ProfileButton";
import CircularProgress from "../components/ui/CircularProgress";
import UpgradeModal from "../components/learn/UpgradeModal";
import SubscribedModal from "../components/learn/SubscribedModal";
import "./Learn.css";
import TangledTree from "../components/learn/Tree";
import TreeProgressBanner from "../components/learn/TreeProgressBanner";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {getJob, getMe, logout} from "../api/Client";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import QuizPanel from "../components/learn/QuizPanel";
import NewTree from "../components/learn/NewTree";
import TreeGenerationStatus from "../components/learn/TreeGenerationStatus";

export default function Learn() {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();

    // Stripe's checkout success_url points at this exact path — it's a real
    // URL (not just component state) so the redirect has something to land
    // on, shown as a modal over whatever the Learn page would otherwise render.
    const isThankYouPage = location.pathname === "/learn/thank-you";

    // The checkout just changed `subscribed` server-side — refresh the
    // cached /me so the sidebar's upgrade panel, daily limits, etc. all
    // reflect it immediately instead of waiting on their own refetch.
    useEffect(() => {
        if (isThankYouPage) {
            queryClient.invalidateQueries({ queryKey: ["me"] });
        }
    }, [isThankYouPage, queryClient]);

    const mutation = useMutation({
        mutationFn: () =>
            logout(),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["me"] });
            navigate("/", { replace: true });
        },
    });

    const { jobId, chapterIndex: chapterIndexParam } = useParams<{ jobId: string; chapterIndex?: string }>();

    const {data, isLoading} = useQuery({
        queryKey: ["job", jobId],
        queryFn: () => getJob(jobId!),
        enabled: !!jobId, // Don't run until jobId exists
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            return status && status !== "COMPLETE" && status !== "ERROR" ? 2000 : false;
        },
    });

    // The sidebar's tree list polls the same job's status independently
    // (its own ["jobs"] query, its own 2s timer, started at a different
    // mount time) — without this, whichever of the two happens to poll
    // first shows a status the other hasn't caught up to yet (e.g. the
    // sidebar's "?" icon appearing before this panel shows the QuizPanel).
    // Re-runs only when the status value itself changes, so this can't
    // become a refetch loop between the two queries.
    useEffect(() => {
        if (jobId && data?.status) {
            queryClient.invalidateQueries({ queryKey: ["jobs"] });
        }
    }, [jobId, data?.status, queryClient]);

    // The chapter the tree is currently showing lives in the URL (so it
    // survives refresh/back-forward), not in component state. No segment
    // means chapter 0 (e.g. a freshly-created tree at /learn/:jobId).
    const parsedChapterIndex = Number(chapterIndexParam);
    const chapterIndex = chapterIndexParam !== undefined && Number.isInteger(parsedChapterIndex)
        ? parsedChapterIndex
        : 0;

    // Chapters/nodes are only ever created once, all at once, right before the
    // job flips to COMPLETE — so until then there's nothing tree-shaped to show.
    const isTreeReady = data?.status === "COMPLETE";

    const chapters = data?.subject?.chapters ?? [];
    const currentChapter = chapters[chapterIndex];
    const hasPreviousChapter = chapterIndex > 0;
    const hasNextChapter = chapterIndex < chapters.length - 1;
    const isCurrentChapterComplete = isChapterComplete(currentChapter);
    // The tree only ever shows one chapter at a time, so a chapter with no
    // node groups yet just means its content hasn't loaded/generated in —
    // not that it's genuinely empty. Show a spinner instead of an empty tree.
    const isCurrentChapterLoading = isChapterLoading(currentChapter, chapterIndex, chapters.length);

    // You can't view a chapter before finishing the ones ahead of it — if the
    // URL points further than the earliest unfinished chapter, snap back to
    // that one instead of letting the tree render the wrong (locked) chapter.
    useEffect(() => {
        if (!isTreeReady || chapters.length === 0) return;

        const unlockedIndex = firstUnfinishedChapterIndex(chapters);

        // No chapter in the URL at all — selecting this tree from the
        // sidebar (or following the bare /learn/:jobId link) lands here.
        // Jump straight to the chapter the user's actually up to instead of
        // always defaulting to chapter 0.
        if (chapterIndexParam === undefined && unlockedIndex !== 0) {
            navigate(`/learn/${jobId}/chapter/${unlockedIndex}`, { replace: true });
            return;
        }

        if (chapterIndex > unlockedIndex) {
            navigate(`/learn/${jobId}/chapter/${unlockedIndex}`, { replace: true });
        }
    }, [jobId, chapterIndex, chapterIndexParam, chapters, isTreeReady]);

    // Same cache entry ProfileButton reads — daily limit booleans live here
    // (backend-computed, not derived from raw counts on the frontend).
    const {data: me} = useQuery({
        queryKey: ["me"],
        queryFn: getMe,
    });

    const avgGrade = averageGrade(data?.subject?.chapters);

    // Gate on new-tree / open-problem clicks for unsubscribed users who've
    // hit today's free limit. The backend endpoints still enforce this for
    // real (this is only a pre-check for instant feedback), so it's fine to
    // fail open — e.g. while `me` hasn't loaded yet — rather than block.
    const [paywall, setPaywall] = useState<{ open: boolean; reason?: string }>({ open: false });

    const handleLogout = () => {
        mutation.mutate()
    };

    const handleNewTree = () => {
        // /me always resolves 200 — {loggedIn: false} is a real, valid
        // response, not a "still loading" one — so the limit check only
        // makes sense for an actual logged-in, known-unsubscribed user.
        if (me?.loggedIn && !me.subscribed && me.canCreateTree === false) {
            setPaywall({ open: true, reason: "You've used today's free trees." });
            return;
        }
        navigate("/learn")
    };

    const handleOpenNode = (id: string) => {
        if (me?.loggedIn && !me.subscribed && me.canOpenProblem === false) {
            setPaywall({ open: true, reason: "You've used today's free problems." });
            return;
        }
        navigate(`/learn/${jobId}/problem/${id}`);
    };

    const sidebarProps = {
        onSelectTree: (id: string) => navigate(`/learn/${id}`),
        onRenameTree: (id: string) => console.log("rename", id),
        onDeleteTree: (id: string) => console.log("delete", id),
    };

    return (
        <div className="learn-page">
            <aside className="learn-page__sidebar">
                <SidebarHeader/>
                <NewTreeButton onClick={handleNewTree}/>
                <TreeList {...sidebarProps}/>
            </aside>

            <main className="learn-page__main">
                <header className="learn-page__header">
                    <MobileSidebarMenu
                        onLogout={handleLogout}
                        onNewTree={handleNewTree}
                        {...sidebarProps}
                    />
                    <ProfileButton
                        onLogout={handleLogout}
                        className="learn-page__profile-button"
                    />
                    <UpgradeModal
                        open={paywall.open}
                        onClose={() => setPaywall({ open: false })}
                        reason={paywall.reason}
                    />
                    <SubscribedModal
                        open={isThankYouPage}
                        onClose={() => navigate("/learn", { replace: true })}
                    />
                    {jobId
                        ? (isTreeReady
                            ? <TreeProgressBanner chapterIndex={chapterIndex}/>
                            : <h1 className="learn-page__title">
                                {data?.status === "QUESTIONS" ? "A few questions first" : "Generating your tree"}
                              </h1>)
                        : <h1 className="learn-page__title">New tree</h1>
                    }
                </header>

                <div className="learn-page__scroll">
                    {jobId
                        ? (isTreeReady
                            ? (isCurrentChapterLoading
                                ? <TreeGenerationStatus status={undefined}/>
                                : currentChapter &&
                                    <div className="learn-page__tree">
                                        <TangledTree
                                            onOpenNode={handleOpenNode}
                                            nodes={flattenNodeGroups(currentChapter.nodeGroups).map((node: any) => {
                                                return ({
                                                    id: node.id,
                                                    title: node.title,
                                                    parents: node.parents,
                                                    markedRead: node.markedDone,
                                                    groupTitle: node.groupTitle,
                                                })
                                            }) || []}
                                            beforeContent={hasPreviousChapter && (
                                                <div className="learn-page__chapter-arrow-row learn-page__chapter-arrow-row--prev">
                                                    <button
                                                        type="button"
                                                        className="learn-page__chapter-arrow"
                                                        aria-label="Previous chapter"
                                                        title="Previous chapter"
                                                        onClick={() => navigate(`/learn/${jobId}/chapter/${chapterIndex - 1}`)}
                                                    >
                                                        <ChevronIcon direction="up"/>
                                                    </button>
                                                </div>
                                            )}
                                            afterContent={hasNextChapter && (
                                                <div className="learn-page__chapter-arrow-row learn-page__chapter-arrow-row--next">
                                                    <button
                                                        type="button"
                                                        className="learn-page__chapter-arrow"
                                                        aria-label="Next chapter"
                                                        title={isCurrentChapterComplete ? "Next chapter" : "Finish this chapter first"}
                                                        disabled={!isCurrentChapterComplete}
                                                        onClick={() => navigate(`/learn/${jobId}/chapter/${chapterIndex + 1}`)}
                                                    >
                                                        <ChevronIcon direction="down"/>
                                                    </button>
                                                </div>
                                            )}
                                        />
                                    </div>)
                            : (data?.status === "QUESTIONS"
                                ? <QuizPanel/>
                                : <TreeGenerationStatus status={data?.status}/>))
                        : <NewTree/>
                    }
                </div>
            </main>
        </div>
    );
}

/** Small chevron used for the chapter-nav arrows — plain currentColor SVG
 *  so it always matches the button's own color (same as the tree nodes). */
function ChevronIcon({ direction }: { direction: "up" | "down" }) {
    return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path
                d={direction === "up" ? "M5 15 L12 8 L19 15" : "M5 9 L12 16 L19 9"}
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

type Node = {
    id: string;
    title: string;
    index: number;
    text: string | null;
    answer: string | null;
    answerGrade: number | null;
    answerNotes: string | null;
    generationInput: string;
};

type NodeGroup = {
    id: string;
    concept: string;
    generationInput: string;
    index: number;
    nodes: Node[];
};

type FlatNode = {
    id: string;
    title: string;
    index: number;
    text: string | null;
    markedDone: boolean;
    answerGrade: number | null;
    generationInput: string;
    parents: string[];
    /** Set only on a group's first problem, so the tree can show the group's
     *  name as a heading above it. */
    groupTitle?: string;
};

// There's no group->group or node->node linking from the backend anymore —
// groups and their nodes are each just ordered by `index`. The whole subject
// is one continuous chain: sort groups, sort each group's nodes, and wire
// every node to whichever node immediately precedes it in that order. A
// group contributes only its own problems (no separate node for the group
// itself); its title rides along on the first of those problems instead.
function flattenNodeGroups(nodeGroups: NodeGroup[]): FlatNode[] {
    const sortedGroups = [...nodeGroups].sort((a, b) => a.index - b.index);

    const result: FlatNode[] = [];
    let previousNodeId: string | null = null;

    for (const group of sortedGroups) {
        const sorted = [...(group.nodes ?? [])].sort((a, b) => a.index - b.index);

        sorted.forEach((node, i) => {
            result.push({
                ...node,
                parents: previousNodeId ? [previousNodeId] : [],
                markedDone: node.answerGrade !== null,
                groupTitle: i === 0 ? group.concept : undefined,
            });
            previousNodeId = node.id;
        });
    }

    return result;
}

// Walks every chapter's node groups' nodes (unlike the tree itself, which
// only ever shows one chapter at a time) and averages every non-null
// answerGrade — null just means "not answered yet", not a zero, so those
// are skipped rather than dragging the average down.
function averageGrade(chapters: { nodeGroups: NodeGroup[] }[] | undefined): number | null {
    const grades = (chapters ?? [])
        .flatMap((chapter) => chapter.nodeGroups ?? [])
        .flatMap((group) => group.nodes ?? [])
        .map((node) => node.answerGrade)
        .filter((grade): grade is number => grade !== null && grade !== undefined);

    if (grades.length === 0) return null;
    return grades.reduce((sum, grade) => sum + grade, 0) / grades.length;
}

// Whether a chapter's very last problem (last node of its last node group,
// both by `index`) has been answered — gates the "next chapter" arrow so
// you can't skip ahead before finishing the one you're on.
function isChapterComplete(chapter: { nodeGroups: NodeGroup[] } | undefined): boolean {
    const groups = chapter?.nodeGroups ?? [];
    if (groups.length === 0) return false;

    const lastGroup = [...groups].sort((a, b) => a.index - b.index).at(-1)!;
    const lastNode = [...(lastGroup.nodes ?? [])].sort((a, b) => a.index - b.index).at(-1);

    return lastNode?.answerGrade !== null && lastNode?.answerGrade !== undefined;
}

// True while the requested chapter is a real, in-range chapter that just
// doesn't have its node groups populated yet. An index past the end of the
// array isn't "loading" — that's an invalid URL, and there's nothing to spin
// on — so this only fires for a chapter that exists but is still empty.
function isChapterLoading(
    chapter: { nodeGroups: NodeGroup[] } | undefined,
    chapterIndex: number,
    chapterCount: number,
): boolean {
    if (chapterIndex < 0 || chapterIndex >= chapterCount) return false;
    return !chapter || (chapter.nodeGroups ?? []).length === 0;
}

// The earliest chapter (by array order) that hasn't been finished yet — i.e.
// the furthest chapter the URL is allowed to point at. Falls back to the
// last chapter once every chapter is complete.
function firstUnfinishedChapterIndex(chapters: { nodeGroups: NodeGroup[] }[]): number {
    const index = chapters.findIndex((chapter) => !isChapterComplete(chapter));
    return index === -1 ? chapters.length - 1 : index;
}